// REPLACE: Entire file
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useContainerWidth } from "src/components/Hooks/useContainerWidth";
import { useContainerHeight } from "src/components/Hooks/useContainerHeight";
import { hasLowNSize } from "../BarCharts/chartHelpers";
import { ChartContainer } from "../components/ChartContainer";
import { ChartFootnote } from "../components/ChartFootnote";
import ChartHeader from "../components/ChartHeader";
import ChartTabularView from "../components/ChartTabularView";
import { ChartRenderedFromType, IStatTestConfig } from "src/pages/Insights/models/interfaces";
import { useScaledConfig, TooSmallFallback } from "../ChartScaleContext";
import { pxToRem } from "src/pages/Insights/transformer/dashboardUtils";
import { useSelector } from "react-redux";
import { RootState } from "src/store";

interface ITableAsChartProps {
	chartResponse: any;
	chartRules?: Record<string, any>;
	isAISummaryAvailable?: boolean;
	isChartView?: boolean;
	setIsChartView?: (value: boolean) => void;
	renderedFrom?: ChartRenderedFromType;
}

/**
 * Renders chart data as a table. Supports both single-chart and multi-tile modes.
 * In single-chart mode, no scaling overrides are passed — ChartTabularView uses
 * its own tableConfig defaults, keeping behaviour identical to before.
 * In multi-tile mode, scaled values from ChartScaleProvider flow through.
 */
const TableAsChart: React.FC<ITableAsChartProps> = ({ chartResponse, chartRules, isAISummaryAvailable = false, isChartView, setIsChartView, renderedFrom }) => {
	// Early validation
	if (!chartResponse?.data?.[0]?.chart_data) {
		console.error("TableAsChart: No chart data available", { chartResponse });
		return <div className="p-4 text-red-500">TableAsChart: No chart data available</div>;
	}

	const scaled = useScaledConfig();
	const { isMultiTileSlide } = useSelector((state: RootState) => state.quantDashboardSlice);
	const statTestConfig: IStatTestConfig | null = chartResponse.chart_config?.stat_test_config ?? null;

	const apiData = chartResponse?.data?.[0]?.chart_data;
	const colors = chartResponse?.chart_config?.ser_color;
	const labelFormat = chartResponse.chart_config.dlabel_fmt;

	const [visibleProducts, setVisibleProducts] = useState<string[]>([]);
	const [expanded, setExpanded] = useState(false);

	const containerRef = useRef<HTMLDivElement>(null);
	const headerRef = useRef<HTMLDivElement>(null);
	const footnoteRef = useRef<HTMLDivElement>(null);
	const tableViewContainerRef = useRef<HTMLDivElement>(null);

	const containerWidth = useContainerWidth(containerRef);
	const tableViewContainerHeight = useContainerHeight(tableViewContainerRef);

	useEffect(() => {
		const initialVisible = Object.keys(chartResponse?.chart_config?.ser_color);
		setVisibleProducts(initialVisible);
	}, [chartResponse?.chart_config?.ser_color]);

	const showLowNSize = useMemo(() => hasLowNSize(apiData, { threshold: 30 }), [apiData]);

	/** Show fallback when tile is too small to render a readable table */
	if (scaled.isTooSmall) {
		return <TooSmallFallback title={chartResponse.chart_title} />;
	}

	/**
	 * Build scaling overrides ONLY for multi-tile mode.
	 * In single-chart mode this stays undefined → ChartTabularView
	 * uses its own tableConfig defaults with zero behavioural change.
	 */
	// In TableAsChart, update multiTileOverrides:
	const multiTileOverrides = isMultiTileSlide
		? {
				dataFontSize: scaled.typography.dataLabelTbl,
				headerFontSize: scaled.typography.axisLabelTbl,
				nSizeFontSize: scaled.typography.nSize,
				cellPaddingV: scaled.geometry.cellPaddingV,
				cellPaddingH: scaled.geometry.cellPaddingH,
				maxEllipsisLines: 2,
				scaleFactor: scaled.scaleFactor,
				isMultiTile: true,
			}
		: undefined;

	return (
		<ChartContainer innerRef={containerRef} scaledPadding={isMultiTileSlide ? scaled.layout.containerPadding : undefined} renderedFrom={renderedFrom}>
			{/* HEADER: Title + Legend */}
			<div ref={headerRef}>
				<ChartHeader
					title={chartResponse.chart_title}
					ai_header={chartResponse?.ai_header}
					subtitle={chartResponse.chart_subtitle}
					legendWaves={colors}
					visibleWaves={visibleProducts}
					setVisibleWaves={setVisibleProducts}
					expanded={expanded}
					setExpanded={setExpanded}
					layout="center"
					isAISummaryAvailable={isAISummaryAvailable}
					isChartView={false}
					setIsChartView={setIsChartView}
					showToggle={false}
					{...(isMultiTileSlide && {
						scaledTitleFontSize: pxToRem(scaled.typography.title),
						scaledLegendFontSize: scaled.typography.legend,
						showSubtitle: scaled.layout.showSubtitle,
						showLegend: scaled.layout.showLegend,
						legendLines: scaled.layout.legendLines,
					})}
					renderedFrom={renderedFrom}
				/>
			</div>

			{/* TABLE CONTENT */}
			<div
				ref={tableViewContainerRef}
				style={{
					flex: 1,
					display: "flex",
					flexDirection: "column",
					minHeight: 0,
					overflow: "hidden",
					justifyContent: "center",
					alignItems: "center",
				}}>
				<ChartTabularView
					chartData={apiData}
					chartType="v_stacked_column"
					tableContainerHeight={tableViewContainerHeight}
					parentContainerWidth={containerWidth}
					labelFormat={labelFormat}
					statTestConfig={statTestConfig}
					renderedFrom={renderedFrom}
					scaledOverrides={multiTileOverrides}
				/>
			</div>

			{/* FOOTNOTE — hidden in multi-tile (showFootnote is false) */}
			{(!isMultiTileSlide || scaled.layout.showFootnote) && (
				<div ref={footnoteRef}>
					<ChartFootnote
						src={chartResponse.footnote?.src}
						footnoteObj={chartResponse.footnote}
						analysis_name={chartResponse.analysis_type}
						hasLowNSize={showLowNSize}
						stat_message={chartResponse.footnote?.stat_message}
						isWeighted={chartResponse.is_weight}
						className="py-3 mt-auto"
						valueParams={chartResponse?.value_params}
						renderedFrom={renderedFrom}
					/>
				</div>
			)}
		</ChartContainer>
	);
};

export default TableAsChart;
